import { makeAutoObservable, runInAction } from 'mobx'

import { serverMessage, serverStatus } from '@/Ship/Helpers/serverError'

import { MIN_PASSWORD_LENGTH, type PasswordRecoveryStep } from '../Contracts/IPasswordRecovery'
import { RequestPasswordResetTask } from '../Tasks/RequestPasswordResetTask'
import { ResetPasswordTask } from '../Tasks/ResetPasswordTask'

/** Тексти форми (`SPEC-005` §7.4). */
export const PASSWORD_RECOVERY_TEXTS = {
    notFound: 'Користувача з таким E-Mail не знайдено.',
    codeSent: 'Інформацію про відновлення успішно відправлено Вам на пошту.',
    mismatch: 'Паролі не збігаються.',
    tooShort: `Пароль має містити щонайменше ${MIN_PASSWORD_LENGTH} символів.`,
    done: 'Пароль успішно змінено. Тепер ви можете увійти.',
    unknown: 'Сталася невідома помилка. Будь ласка, спробуйте пізніше.',
} as const

interface ResetValues {
    code: string
    plainPassword: string
    confirmPassword: string
}

/**
 * Стан форми відновлення пароля: крок, токен кроку 2, очікування й помилка.
 *
 * Живе на один екземпляр форми — токен відновлення не повинен пережити
 * сторінку: він дає змінити пароль будь-кому, хто знає код.
 */
export class PasswordRecoveryManager {
    step: PasswordRecoveryStep = 'email'
    isLoading = false
    error: string | null = null
    /** Пошта, на яку пішов код, — щоб людина бачила, куди дивитися. */
    email = ''

    private token: string | null = null

    constructor() {
        makeAutoObservable(this)
    }

    /** Крок 1: лист із кодом. */
    async requestCode(email: string): Promise<void> {
        this.isLoading = true
        this.error = null

        try {
            const token = await RequestPasswordResetTask(email)

            runInAction(() => {
                this.token = token
                this.email = email.trim().toLowerCase()
                this.step = 'code'
            })
        } catch (error) {
            runInAction(() => {
                // 404 — і «такої пошти немає», і «співробітник деактивований»:
                // текст легасі, лише на цей випадок (`SPEC-005` §7.4). Решта
                // (ліміт запитів, валідація) — поясненням сервера
                this.error =
                    serverStatus(error) === 404
                        ? PASSWORD_RECOVERY_TEXTS.notFound
                        : serverMessage(error, PASSWORD_RECOVERY_TEXTS.unknown)
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    /**
     * Крок 2: код і новий пароль.
     *
     * Розбіжність паролів і закороткий пароль ловимо до запиту: перше бек не
     * перевіряє взагалі (підтвердження до нього не доходить), друге однаково
     * повернув би 422.
     */
    async resetPassword({ code, plainPassword, confirmPassword }: ResetValues): Promise<void> {
        if (plainPassword !== confirmPassword) {
            this.error = PASSWORD_RECOVERY_TEXTS.mismatch
            return
        }

        if (plainPassword.length < MIN_PASSWORD_LENGTH) {
            this.error = PASSWORD_RECOVERY_TEXTS.tooShort
            return
        }

        if (!this.token) {
            this.back()
            return
        }

        this.isLoading = true
        this.error = null

        try {
            await ResetPasswordTask({ token: this.token, code, plainPassword })

            runInAction(() => {
                this.token = null
                this.step = 'done'
            })
        } catch (error) {
            runInAction(() => {
                // «Невірний код…», «Термін дії коду минув…», помилки пароля —
                // українською з Ship-перекладів бека
                this.error = serverMessage(error, PASSWORD_RECOVERY_TEXTS.unknown)
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    /** «Назад»: до кроку 1, старий токен більше не потрібен. */
    back(): void {
        this.token = null
        this.error = null
        this.step = 'email'
    }
}
